// Standalone Socket.io client for the SQ-AI sandbox page.
//
// Why not reuse chart-agent's chartAgentSocket.js? That module is the dev UI's
// socket — its cached sockets share localStorage keys with that page (env,
// confidentialAccess), so a CEO toggling something here would silently change
// the dev UI's controls and vice versa. Keeping a separate connection keeps
// the two surfaces from crossing wires while we have both deployed.
//
// Same handshake shape as chartAgentSocket.js (token, env, uiMode,
// confidentialAccess) so the /ai-assistant namespace middleware validates us
// the same way it validates the dev UI.

import { io } from "socket.io-client";

let socket = null;
// Stash whatever auth values the caller hands us; refreshAuth rebuilds the
// handshake from these on each reconnect. Kept out of localStorage on purpose
// — the sandbox page owns its own state and shouldn't share keys with the
// chart-agent dev UI.
let currentAuth = {
  token: null,
  env: "dev",
  uiMode: "prod",
  confidentialAccess: false,
  // Querying user's IANA timezone — the agent converts UTC→this zone and truncates to date for
  // all date comparisons/bucketing (mirroring the dashboard). Sandbox default Asia/Calcutta
  // matches the known SafetyQube token; in production the token's selectedTimezone claim wins.
  userTz: "Asia/Calcutta",
};

function buildAuth() {
  return {
    token: localStorage.getItem("jwt_token"),
    env: currentAuth.env,
    uiMode: currentAuth.uiMode,
    confidentialAccess: currentAuth.confidentialAccess === true,
    userTz: currentAuth.userTz,
    // Dev/test UI: opt in to receiving each query's generated SQL on
    // ai:tool_call (the SQL accordion). Independent of uiMode. The production
    // SQ integration omits this and never receives SQL.
    devSql: true,
  };
}

export function getSocket() {
  if (socket) return socket;
  socket = io("/ai-assistant", {
    autoConnect: true,
    auth: buildAuth(),
    reconnection: true,
    reconnectionAttempts: 5,
    reconnectionDelay: 1000,
  });
  socket.on("connect", () => console.log("[sq-ai-socket] connected", socket.id));
  socket.on("connect_error", (err) => console.error("[sq-ai-socket] connect_error:", err.message, err.data));
  socket.on("disconnect", (reason) => console.log("[sq-ai-socket] disconnect:", reason));
  return socket;
}

// Set the env / confidentialAccess values that will be used on the next
// (re)connect. The page calls refreshAuth() after a control changes so the
// new handshake takes effect.
export function setAuth({ env, confidentialAccess, userTz }) {
  if (env != null) currentAuth.env = env;
  if (confidentialAccess != null) currentAuth.confidentialAccess = confidentialAccess === true;
  if (userTz != null) currentAuth.userTz = userTz;
}

export function refreshAuth() {
  if (!socket) return;
  socket.auth = buildAuth();
  socket.disconnect();
  socket.connect();
}

export function disconnectSocket() {
  if (!socket) return;
  socket.disconnect();
  socket = null;
}
